import React, { CSSProperties, PropsWithChildren, useEffect, useRef, useState } from "react"
import "./hover-tooltip.css"
import classNames from "classnames"

export enum HoverTooltipPosition {
    top = "top",
    right = "right",
    bottom = "bottom",
    left = "left",
    bottomRight = "bottomRight",
}

const getTooltipPositioningStyles = (tooltipPositioning: HoverTooltipPosition, parentWidth = 0) => {
    if (tooltipPositioning === HoverTooltipPosition.top) {
        return {
            transform: `translateX(calc(50% + ${parentWidth / 2}px)) translateY(calc(-1 * 100%))`,
        }
    }
}

const getNotchPositioningStyles = (tooltipPositioning: HoverTooltipPosition, parentWidth = 0) => {
    if (tooltipPositioning === HoverTooltipPosition.top) {
        return {
            transform: `translateX(calc(${parentWidth / 2}px - 50%)) translateY(-17px)`,
        }
    }
}

type HoverTooltipProps = {
    id: string
    parentRef: React.MutableRefObject<HTMLDivElement | null>
    hoverTooltipPosition?: HoverTooltipPosition
    borderColor?: string
    currentHoveredTooltip?: string
    style?: CSSProperties
    fixedPositioning?: boolean
    containerClassName?: string
    hoverTimeoutDelay?: number
    hoverStartDelay?: number
    borderless?: boolean
} & PropsWithChildren

const hoverTimeoutDelayInMs = 1000

export const HoverTooltip = ({
    id,
    parentRef,
    hoverTooltipPosition = HoverTooltipPosition.bottom,
    borderColor,
    currentHoveredTooltip,
    style,
    fixedPositioning,
    children,
    containerClassName,
    hoverTimeoutDelay = hoverTimeoutDelayInMs,
    hoverStartDelay = 0,
    borderless,
}: HoverTooltipProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const startTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const tooltipRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const parent = parentRef.current
        if (!parent) return
        const handleMouseEnter = () => {
            if (startTimerRef.current && !timerRef.current) return
            startTimerRef.current = setTimeout(() => setIsOpen(true), hoverStartDelay)

            if (timerRef.current !== null) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }
        const handleMouseLeave = () => {
            if (startTimerRef.current) clearTimeout(startTimerRef.current)
            timerRef.current = setTimeout(() => {
                setIsOpen(false)
                timerRef.current = null
                startTimerRef.current = null
            }, hoverTimeoutDelay)

            return () => {
                if (timerRef.current) clearTimeout(timerRef.current)
            }
        }
        parent.addEventListener("mouseenter", handleMouseEnter)
        parent.addEventListener("mouseout", handleMouseLeave)
        return () => {
            if (parent) {
                parent.removeEventListener("mouseenter", handleMouseEnter)
                parent.removeEventListener("mouseout", handleMouseLeave)
            }
        }
    }, [parentRef, hoverStartDelay, hoverTimeoutDelay])

    useEffect(() => {
        if (!isOpen || !fixedPositioning) return
        const menu = parentRef.current

        if (!menu || !tooltipRef.current) return
        const { top, left } = menu.getBoundingClientRect()
        const topPosition = top + 25
        const leftPosition = left

        tooltipRef.current.setAttribute("style", `top: ${topPosition}px; left: ${leftPosition}px`)

        //close the tooltip if we start scrolling
        const closeMenu = () => setIsOpen(false)
        window.addEventListener("wheel", closeMenu)
        return () => {
            window.removeEventListener("wheel", closeMenu)
        }
    }, [isOpen, fixedPositioning, parentRef])

    useEffect(() => {
        if (currentHoveredTooltip !== id) {
            setIsOpen(false)
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }
    }, [currentHoveredTooltip, id])

    if (!isOpen) {
        return null
    }
    const parentRect = parentRef.current?.getBoundingClientRect()
    const tooltipPositioningStyles = getTooltipPositioningStyles(
        hoverTooltipPosition,
        parentRect?.width
    ) as CSSProperties
    const notchPositioningStyles = getNotchPositioningStyles(
        hoverTooltipPosition,
        parentRect?.width
    )
    return (
        <div className={classNames("fixed z-[60]", containerClassName)} ref={tooltipRef}>
            {hoverTooltipPosition === HoverTooltipPosition.bottom && (
                <div className={classNames("hover-tooltip__notch--bottom")} />
            )}
            {hoverTooltipPosition === HoverTooltipPosition.bottom && !borderless && (
                <div
                    className={classNames("hover-tooltip__inner-notch--bottom")}
                    style={{ borderBottomColor: borderColor }}
                />
            )}
            {hoverTooltipPosition === HoverTooltipPosition.top && (
                <div
                    className={classNames("hover-tooltip__notch--top")}
                    style={{ ...notchPositioningStyles }}
                />
            )}
            {hoverTooltipPosition === HoverTooltipPosition.top && !borderless && (
                <div
                    className={classNames("hover-tooltip__inner-notch--top")}
                    style={{ ...notchPositioningStyles, borderTopColor: borderColor }}
                />
            )}
            {hoverTooltipPosition === HoverTooltipPosition.bottomRight && !borderless && (
                <div
                    className={classNames("hover-tooltip__notch--bottom-right")}
                    style={{ ...notchPositioningStyles, borderTopColor: borderColor }}
                />
            )}

            <div
                className={classNames("hover-tooltip", {
                    "hover-tooltip--borderless": borderless,
                    "hover-tooltip--left": hoverTooltipPosition === HoverTooltipPosition.left,
                    "hover-tooltip--bottom": hoverTooltipPosition === HoverTooltipPosition.bottom,
                    "hover-tooltip--top": hoverTooltipPosition === HoverTooltipPosition.top,
                })}
                style={{
                    ...style,
                    ...tooltipPositioningStyles,
                    borderColor,
                }}
            >
                {children}
            </div>
            {hoverTooltipPosition === HoverTooltipPosition.left && (
                <div className={"hover-tooltip__notch--left"} />
            )}
            {hoverTooltipPosition === HoverTooltipPosition.left && !borderless && (
                <div
                    className={"hover-tooltip__inner-notch--left"}
                    style={{ borderLeftColor: borderColor }}
                />
            )}
        </div>
    )
}
